/**
 * useWorkspaceMembership Hook
 *
 * Fetches the current user's workspace membership information.
 * Provides role, permissions, and computed helper properties.
 *
 * Usage:
 * ```tsx
 * const { role, canEdit, isOwner, isLoading } = useWorkspaceMembership();
 *
 * if (!canEdit) {
 *   return <ReadOnlyView />;
 * }
 * ```
 */

import { useQuery } from '@tanstack/react-query';
import type { WorkspaceRole } from '@loader/shared-types';

import { queryKeys } from '@/lib/queryKeys';
import { useRpc } from '@/lib/rpc';
import { useWorkspaceStore } from '@/store/useWorkspaceStore';

/**
 * Cache duration for membership data (5 minutes)
 * Membership rarely changes during a session
 */
export const MEMBERSHIP_CACHE_TIME = 5 * 60 * 1000;

/**
 * Membership data returned from the API
 */
export interface WorkspaceMembershipData {
  workspace: {
    id: string;
    name: string;
  };
  membership: {
    role: WorkspaceRole;
  };
  user: {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
  };
  storage?: {
    usage: number;
    limit: number | null;
    available: number | null;
    percent_used: number;
  };
}

/**
 * Return type for useWorkspaceMembership hook
 */
export interface UseWorkspaceMembershipReturn {
  /** Full membership data from API */
  data: WorkspaceMembershipData | null | undefined;
  /** User's role in the current workspace */
  role: WorkspaceRole | null;
  /** Whether user can edit (owner or editor) */
  canEdit: boolean;
  /** Whether user is the workspace owner */
  isOwner: boolean;
  /** Whether user is a viewer (read-only access) */
  isViewer: boolean;
  /** Whether membership data is loading */
  isLoading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Refetch membership data */
  refetch: () => void;
}

/**
 * Hook to fetch current user's workspace membership
 *
 * Provides role-based permission helpers for UI rendering decisions.
 * Uses TanStack Query with a 5-minute cache for optimal performance.
 */
export function useWorkspaceMembership(): UseWorkspaceMembershipReturn {
  const rpc = useRpc();
  const { selectedWorkspaceId } = useWorkspaceStore();

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    // Include workspace ID in key so cache is workspace-scoped
    queryKey: queryKeys.workspace.membership(selectedWorkspaceId!),
    queryFn: async (): Promise<WorkspaceMembershipData | null> => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces.me.$get();
      if (!res.ok) return null;
      const response = await res.json();
      // Check for error response
      if ('error' in response) {
        return null;
      }
      return response as WorkspaceMembershipData;
    },
    staleTime: MEMBERSHIP_CACHE_TIME,
    // Don't fetch until a workspace is selected
    enabled: !!selectedWorkspaceId,
  });

  const role = data?.membership?.role ?? null;
  const isOwner = role === 'owner';
  const canEdit = role === 'owner' || role === 'editor';
  const isViewer = role === 'viewer';

  return {
    data,
    role,
    canEdit,
    isOwner,
    isViewer,
    isLoading,
    error: error as Error | null,
    refetch,
  };
}
